import { AloftPrice } from '@aloft/shared-objects';
import { Text, View } from '@react-pdf/renderer';

import { salesComparisonPageStyles as styles } from '../styles.ts';
import {
  AdditionalFeatureKey,
  SalesComparisonAdjustmentKey,
  SalesComparisonGridColumnData,
} from '../types.ts';
import { getSalesComparisonNestedValue } from '../utils/getSalesComparisonNestedValue.ts';

const Label = ({ text }: { text: string[] }) => {
  const labels = text.map((t) => (
    <View style={{ ...styles.label }} key={`label-${text.join('-')}`}>
      <Text style={styles.text}>{t}</Text>
    </View>
  ));

  return <View style={styles.column}>{labels}</View>;
};

const Value = ({
  comp,
  keys,
  align,
}: {
  comp: SalesComparisonGridColumnData;
  keys: (keyof SalesComparisonGridColumnData | AdditionalFeatureKey)[];
  align: 'left' | 'right' | 'center';
}) => {
  const values = keys.map((t) => (
    <View style={{ ...styles.value, textAlign: align }} key={`label-${keys.join('-')}`}>
      <Text style={styles.text}>{getSalesComparisonNestedValue(comp, t)}</Text>
    </View>
  ));

  return <View style={styles.column}>{values}</View>;
};

const Adjustment = ({
  subject,
  comp,
  fields,
  adjustment,
}: {
  subject: SalesComparisonGridColumnData;
  comp: SalesComparisonGridColumnData;
  fields: (keyof SalesComparisonGridColumnData | AdditionalFeatureKey)[];
  adjustment: SalesComparisonAdjustmentKey;
}) => {
  const hasValueDifference = fields.some((k) => {
    const subjectValue = getSalesComparisonNestedValue(subject, k);
    const compValue = getSalesComparisonNestedValue(comp, k);
    return subjectValue && compValue && subjectValue !== compValue;
  });

  const rawAdjustment = comp?.adjustments?.[adjustment] ?? AloftPrice.zero;
  // Only display $0 adjustments if there is an explicit zero adjustment or if there is a value difference between the subject and comp for the adjusted field(s). This is to avoid confusion around $0 adjustments when there is no actual adjustment being made.
  const displayAdjustment =
    rawAdjustment.equals(AloftPrice.zero) &&
    comp.explicitZeroAdjustments?.[adjustment] &&
    hasValueDifference
      ? '$0'
      : rawAdjustment.toString(true);

  const negativeAdjustment = displayAdjustment?.startsWith('-');
  return (
    <View style={styles.column}>
      <View style={styles.value}>
        <Text
          style={{
            ...styles.text,
            ...(negativeAdjustment ? styles.negativeAdjustment : styles.positiveAdjustment),
            textAlign: 'right',
          }}
        >
          {displayAdjustment}
        </Text>
      </View>
    </View>
  );
};

const SalesComparisonRow = ({
  fields,
  labels,
  subject,
  comparables,
  adjustment,
  borderTop = false,
  bold = false,
  align = 'left',
}: {
  fields: (keyof SalesComparisonGridColumnData | AdditionalFeatureKey)[];
  labels: string[];
  subject: SalesComparisonGridColumnData;
  comparables: SalesComparisonGridColumnData[];
  adjustment?: SalesComparisonAdjustmentKey;
  borderTop?: boolean;
  bold?: boolean;
  align?: 'left' | 'right' | 'center';
}) => (
  <View
    style={{
      ...styles.row,
      ...(bold ? styles.bold : {}),
    }}
  >
    {[subject, ...comparables].map((comp, i) => (
      <View
        style={{
          ...styles.innerRow,
          ...(borderTop ? styles.borderTop : {}),
        }}
        key={`scc-${fields.join('-')}-${comp?.address}`}
      >
        {i === 0 && <Label text={labels} />}
        <Value comp={comp} keys={fields} align={align} />
        {i !== 0 && adjustment && (
          <Adjustment subject={subject} comp={comp} fields={fields} adjustment={adjustment} />
        )}
      </View>
    ))}
  </View>
);

// eslint-disable-next-line import/no-default-export
export default SalesComparisonRow;
